// Node Manager - Handles node CRUD operations
class NodeManager {
    constructor() {
        this.nodes = new Map();
        this.connections = new Map();
    }

    createNode(type, data = {}) {
        const nodeId = this.generateNodeId();
        const node = {
            id: nodeId,
            type: type,
            data: data,
            position: { x: 0, y: 0 },
            inputs: [],
            outputs: [],
            created_at: new Date().toISOString()
        };

        this.nodes.set(nodeId, node);
        return node;
    }

    updateNode(nodeId, updates) {
        const node = this.nodes.get(nodeId);
        if (!node) return false;

        Object.assign(node, updates);
        this.nodes.set(nodeId, node);
        return true;
    }

    deleteNode(nodeId) {
        // Remove connections associated with this node
        this.connections.forEach((connection, connId) => {
            if (connection.source === nodeId || connection.target === nodeId) {
                this.connections.delete(connId);
            }
        });

        // Remove the node
        return this.nodes.delete(nodeId);
    }

    getNode(nodeId) {
        return this.nodes.get(nodeId);
    }

    getAllNodes() {
        return Array.from(this.nodes.values());
    }

    createConnection(sourceId, targetId, sourceOutput = 'output_1', targetInput = 'input_1') {
        const connectionId = `${sourceId}_${targetId}_${Date.now()}`;
        const connection = {
            id: connectionId,
            source: sourceId,
            target: targetId,
            sourceOutput: sourceOutput,
            targetInput: targetInput
        };

        this.connections.set(connectionId, connection);
        return connection;
    }

    deleteConnection(connectionId) {
        return this.connections.delete(connectionId);
    }

    getNodeConnections(nodeId) {
        const connections = [];
        this.connections.forEach(conn => {
            if (conn.source === nodeId || conn.target === nodeId) {
                connections.push(conn);
            }
        });
        return connections;
    }

    getChildNodes(nodeId) {
        const children = [];
        this.connections.forEach(conn => {
            if (conn.source === nodeId) {
                const childNode = this.nodes.get(conn.target);
                if (childNode) {
                    children.push(childNode);
                }
            }
        });
        return children;
    }

    getParentNodes(nodeId) {
        const parents = [];
        this.connections.forEach(conn => {
            if (conn.target === nodeId) {
                const parentNode = this.nodes.get(conn.source);
                if (parentNode) {
                    parents.push(parentNode);
                }
            }
        });
        return parents;
    }

    generateNodeId() {
        return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    exportStructure() {
        return {
            nodes: Array.from(this.nodes.entries()),
            connections: Array.from(this.connections.entries())
        };
    }

    importStructure(structure) {
        if (structure.nodes) {
            this.nodes = new Map(structure.nodes);
        }
        if (structure.connections) {
            this.connections = new Map(structure.connections);
        }
    }

    clear() {
        this.nodes.clear();
        this.connections.clear();
    }

    // Helper methods for tree structure
    getRootNodes() {
        const roots = [];
        this.nodes.forEach(node => {
            const parents = this.getParentNodes(node.id);
            if (parents.length === 0) {
                roots.push(node);
            }
        });
        return roots;
    }

    buildTree(nodeId = null) {
        if (nodeId === null) {
            // Build trees for all root nodes
            const roots = this.getRootNodes();
            return roots.map(root => this.buildTree(root.id));
        }

        const node = this.nodes.get(nodeId);
        if (!node) return null;

        const children = this.getChildNodes(nodeId);
        return {
            ...node,
            children: children.map(child => this.buildTree(child.id))
        };
    }

    validateTree() {
        // Check for cycles
        const visited = new Set();
        const recursionStack = new Set();

        const hasCycle = (nodeId) => {
            visited.add(nodeId);
            recursionStack.add(nodeId);

            const children = this.getChildNodes(nodeId);
            for (const child of children) {
                if (!visited.has(child.id)) {
                    if (hasCycle(child.id)) return true;
                } else if (recursionStack.has(child.id)) {
                    return true;
                }
            }

            recursionStack.delete(nodeId);
            return false;
        };

        const roots = this.getRootNodes();
        for (const root of roots) {
            if (hasCycle(root.id)) {
                return { valid: false, error: 'Cycle detected in workflow' };
            }
        }

        return { valid: true };
    }
}

// Export for use in other modules
window.NodeManager = NodeManager;
export default NodeManager;